import { defineComponent as _defineComponent } from 'vue'
import AdUnit from '@/components/AdUnit.vue';
import LazyClientShow from '@/components/LazyClientShow.vue';
import SimilarTitles from '@/components/SimilarTitles.vue';
import BuyBoxNew from '@/components/buybox-new/BuyBoxNew.vue';
import GuideList from '@/components/editorial/GuideList.vue';
import { useI18n } from '@/helpers/composables/useI18n';
import type { AssertByTypename } from '@/helpers/graphql-model-helper';
import type { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import type { SponsoredAdFragment } from '@/pages/graphql/fragments/SponsoredAd.fragment';
import { GetNodeTitleDetailsQuery } from '@/pages/graphql/queries/GetNodeTitleDetails.query';
import { TitleDetailArticleFragment } from '@/pages/graphql/queries/GetTitleDetailArticle.query';
import { TitleDetailSnippetFragment } from '@/pages/graphql/queries/GetTitleDetailSnippets.query';
import SectionContainer from '@/pages/titleDetailV1/components/SectionContainer.vue';
import TitleDetailJumpLinksV1 from '@/pages/titleDetailV1/v1/TitleDetailJumpLinksV1.vue';
import { computed, defineAsyncComponent, ref, watch } from 'vue';

import BuyBoxBanner from '@/components/buybox-new/BuyBoxBanner.vue';
import { TitleOfferFragment } from '@/components/buybox/graphql/fragments/Offer.fragment';
import { streamingChartCountries } from '@/components/details/streaming-chart/streaming-chart-config';
import { useAppStore, useLanguageStore, useUserStore } from '@/helpers/composables/useStores';
import TitleDetailTitleClipsV1 from '@/pages/titleDetailV1/v1/TitleDetailTitleClipsV1.vue';
import {
	isTitleSeason,
	isTitleShow,
	useTitleDetailInfo,
} from '@/pages/titleDetailV1/v1/composables/useTitleDetailInfo';
import TitleDetailHeroV1 from '@/pages/titleDetailV1/v1/hero/TitleDetailHeroV1.vue';
import TitleDetailTitleSidebarV1 from '@/pages/titleDetailV1/v1/sidebar/TitleDetailTitleSidebarV1.vue';
import { BuyBoxFilterOptions, NewBuyBoxView } from '@/stores/user.store';

import BuyBoxTypeSwitcher from '@/components/buybox-new/BuyBoxTypeSwitcher.vue';
import PromotedBuyBox from '@/components/buybox/PromotedBuyBox.vue';
import TextRecommendationModal from '@/features/why-to-watch/components/TextRecommendationModal.vue';
import TextRecommendations from '@/features/why-to-watch/components/TextRecommendations.vue';
import SeasonListV1 from '@/pages/titleDetailV1/v1/SeasonListV1.vue';

import { preventNonSignedInAction } from '@/helpers/prevent-non-signed-in-action-helper';

import { ModuleContentStreamingCharts } from '@/@types/graphql-types';
import { AccountModes } from '@/enums/login';
import { disableScroll, enableScroll } from '@/helpers/scroll-helper';
import { extractBuyboxOffers } from '@/helpers/title-helper';

import PromotionsConfig from '@/features/promotions/config.json';
import { useTitlePageLoggedInExp } from '@/components/experiments/TitlepageLoggedInExp/composables/useTitlepageLoggedInExp';
// SINGLE_QTV_EXP
import { useSingleQtvExp } from '@/components/experiments/SingleQtvExp/composables/useSingleQtvExp';
import { usePromotion } from '@/helpers/composables/usePromotion';
import { BuyBoxOffers, useBuyBoxData } from '@/components/buybox-new/composables/useBuyboxData';
import { TitleDetail } from '@/interfaces/title-details-graphql';
// SINGLE_QTV_EXP

export default /*#__PURE__*/_defineComponent({
  __name: 'TitleDetailV1',
  props: {
    titleDetailNode: null,
    promotedOffers: { default: () => [] },
    metaData: null,
    article: null,
    snippets: null,
    genresByShortName: null,
    isAVODActive: { type: Boolean },
    additionalContexts: null,
    hidePromotedBuyBox: { type: Boolean },
    _sponsoredAd: { default: undefined },
    entityId: null,
    isLoading: { type: Boolean },
    isPerceivedLoading: { type: Boolean },
    includeTitleModules: { type: Boolean }
  },
  emits: ['buyboxLoading', 'closeTitleDetailModal'],
  setup(__props: any, { emit: emits }) {

const props = __props as { titleDetailNode: AssertByTypename<GetNodeTitleDetailsQuery['node'], 'Movie' | 'Show' | 'Season'> | undefined, promotedOffers: TitleOfferFragment[], metaData: any, article: TitleDetailArticleFragment, snippets: TitleDetailSnippetFragment[], genresByShortName: any, isAVODActive: boolean, additionalContexts: SnowplowContext[], hidePromotedBuyBox: boolean, _sponsoredAd: SponsoredAdFragment | undefined, entityId?: string | undefined, isLoading?: boolean, isPerceivedLoading: boolean, includeTitleModules: boolean };

const TitleDetailLazyWhyToWatchV1 = defineAsyncComponent(
	() => import('@/pages/titleDetailV1/v1/TitleDetailLazyWhyToWatchV1.vue')
);
const TitleDetailStreamingChartV1 = defineAsyncComponent(
	() => import('@/pages/titleDetailV1/v1/streaming-chart/TitleDetailStreamingChartV1.vue')
);
const TitleDetailStreamingChartV2 = defineAsyncComponent(
	() => import('@/pages/titleDetailV1/v1/streaming-chart/TitleDetailStreamingChartV2.vue')
);
const TitleSynopsis = defineAsyncComponent(() => import('@/components/details/TitleSynopsis.vue'));

const TitleDetailAccordion = defineAsyncComponent(
	() => import('@/pages/titleDetailV1/components/TitleDetailAccordion.vue')
);
const TitleDetailArticleBlockV1 = defineAsyncComponent(
	() => import('@/pages/titleDetailV1/v1/TitleDetailArticleBlockV1.vue')
);
const TitleDetailProductionNewsBlockV1 = defineAsyncComponent(
	() => import('@/pages/titleDetailV1/v1/TitleDetailProductionNewsBlockV1.vue')
);
const ArticleBlock = defineAsyncComponent(() => import('@/components/details/editorial/ArticleBlock.vue'));
const UpcomingContent = defineAsyncComponent(() => import('@/components/upcoming/UpcomingContent.vue'));
const EpisodesList = defineAsyncComponent(() => import('@/components/EpisodesList.vue'));

const TitleCredits = defineAsyncComponent(() => import('@/components/details/TitleCredits.vue'));
const SpinningText = defineAsyncComponent(() => import('@/components/details/SpinningText.vue'));
const TitleDetailSimilarTitles = defineAsyncComponent(
	() => import('@/pages/titleDetailV1/v1/TitleDetailSimilarTitles.vue')
);
const TitleDetailSimilarTitlesV2 = defineAsyncComponent(
	() => import('@/pages/titleDetailV1/v1/TitleDetailSimilarTitlesV2.vue')
);
const BuyBox = defineAsyncComponent(() => import('@/components/buybox/BuyBox.vue'));
const TitleDetailQuickActionBarV1 = defineAsyncComponent(
	() => import('@/pages/titleDetailV1/v1/sidebar/TitleDetailQuickActionBarV1.vue')
);

// need to import these immediately because it's above the fold and could cause CLS
const activeClipId = ref<string | null>(null);
const streamingChart = ref<{ renderChart?: boolean } | null>(null);
const container = ref(null);
const displayTextRecommendationModal = ref<boolean>(false);

const { titlePageActiveVariant } = useTitlePageLoggedInExp();

const { newBuyboxView, isAdmin, isEditor, profileIsComplete, profileInfo, loggedInExpBuyboxView } = useUserStore();
const { isSeoUser } = useAppStore();
const { country } = useLanguageStore();
const { t } = useI18n();
const { isLoggedIn } = useUserStore();




const titleDetailNodeComputed = computed(() => props.titleDetailNode);
const {
	title,
	titleName,
	titleId,
	titleObjectId,
	seasonName,
	seasonNumber,
	seasonTotalEpisodeCount,
	isShow,
	isSeason,
	recentEpisodes,
	recentEpisodesAvailable,
	isTitleUpcoming,
	showDetails,
	originalTitle,
	isInWatchList,
} = useTitleDetailInfo(titleDetailNodeComputed);

const textRecommendationUserCountries = PromotionsConfig.w2w.pro_users_countries;

const buyboxOffers = computed(() => {
	if (!props.titleDetailNode) {
		return null;
	}
	return extractBuyboxOffers(props.titleDetailNode);
});

const hasTrailers = computed(() => {
	return titleClips.value.length > 0;
});
const isListSponsoredAd = computed(() => props._sponsoredAd?.campaign?.node?.__typename === 'GenericTitleList');
const isShadowCampaign = computed(() => !!props._sponsoredAd?.bidId && props._sponsoredAd?.campaign === null);
const hasActiveSRCampaign = computed(
	() => props._sponsoredAd?.holdoutGroup == false && props._sponsoredAd?.campaign != null
);

const streamingCharts = computed(() => {
	if (props.includeTitleModules) {
		const streamingChartsModule = props.titleDetailNode?.titleModules.find(
			module => module?.template.technicalName === 'TITLE_STREAMING_CHARTS'
		);
		return (streamingChartsModule?.content as ModuleContentStreamingCharts)?.Titles ?? [];
	}
	return [];
});
// here we only check if the current title has any streaming charts informations.
// it won't have informations about titles up or below the title's streaming charts' rank.
const displayStreamingChart = computed(() => {
	if (!streamingChartCountries.includes(country.value) || !props.titleDetailNode?.streamingCharts) {
		return false;
	}

	const rank = props.titleDetailNode?.streamingCharts.edges?.[0]?.streamingChartInfo?.rank;
	return !!rank && rank < 10000;
});
const visibleSectionState = computed(() => {
	return {
		trailers: !!(hasTrailers.value && props.titleDetailNode),
		'production-news': !!props.article?.content.productionNews,
		'what-to-know': !!props.article?.content.whatToKnow,
		// 'why-to-watch': props.snippets.length > 0,
		'why-to-watch': textRecommendationArray.value?.length > 0,
		synopsis: !!showSynopsis.value || !!props.article,
		'streaming-chart': displayStreamingChart.value && !!streamingChart.value?.renderChart,
		'episodes-lists': !isTitleUpcoming.value && (isSeason.value || isShow.value),
		'regular-buybox': !(isTitleUpcoming.value && !showNewBuyBox.value),
		'similar-content': showSimilarTitles.value,
	};
});

const showSynopsis = computed(
	() =>
		!(props.metaData?.htmlContent?.detail_heading != null && props.metaData?.htmlContent?.detail_content != null) &&
		!!props.titleDetailNode?.content?.shortDescription
);

const showSimilarTitles = computed(() => !!props.titleDetailNode && !props.metaData?.htmlContent.hide_similar_titles);

const aboutHeadline = computed(() => {
	return isSeason.value
		? t('WEBAPP_ABOUT_THE_SEASON')
		: isShow.value
		? t('WEBAPP_ABOUT_THE_TV_SHOW')
		: t('WEBAPP_ABOUT_THE_MOVIE');
});

const showNewBuyBox = computed(() => {
	if (!!titlePageActiveVariant.value && titlePageActiveVariant.value !== 'control') {
		return loggedInExpBuyboxView.value !== NewBuyBoxView.GRID;
	}

	return newBuyboxView.value !== NewBuyBoxView.GRID;
});

const episodesTitle = computed(() => {
	// only show this for now for show page release - tbc
	if (isSeason.value) return t('WEBAPP_X_EPISODES', { episodes: seasonTotalEpisodeCount.value || '' });
	return t('WEBAPP_WATCH_NEWEST_EPISODES');
	// return recentEpisodesAvailable.value ? t('WEBAPP_RECENT_EPISODES') : t('WEBAPP_WATCH_NEWEST_EPISODES');
});

const maxEpisodes = computed(() =>
	recentEpisodesAvailable.value ? recentEpisodes.value.length : seasonTotalEpisodeCount.value
);

const episodes = computed(() =>
	recentEpisodesAvailable.value
		? recentEpisodes.value
		: (props.titleDetailNode as AssertByTypename<GetNodeTitleDetailsQuery['node'], 'Season'>).episodes || []
);

const showTextRecommendationTools = computed(() => {
	if (!isLoggedIn.value) return false;

	if (isAdmin.value || isEditor.value) {
		return true;
	} else if (!textRecommendationUserCountries.includes(country.value)) {
		return false;
	}

	const profileCountry = profileInfo.value?.country;
	return profileIsComplete.value ? isLoggedIn.value && profileCountry === country.value : isLoggedIn.value;
});

// we don't want to display too many ads in one viewport.
// that's why we're taking out the 3rd ad slot, if it's:
// - on a page without streaming charts (that would push down the content vertically)
// - only in DE
const displayThirdAd = computed(() => {
	return !['DE'].includes(country.value) || displayStreamingChart.value;
});

const titleClips = computed(() => {
	// check for localised trailers, if not use fallbacks
	const localisedClips = getTitleClips(props.titleDetailNode, 'content');
	if (localisedClips.length) return localisedClips;

	return getTitleClips(props.titleDetailNode, 'fallBackClips');
});

function getTitleClips(titleNode: typeof props.titleDetailNode, key: 'content' | 'fallBackClips') {
	const youtubeClips = titleNode?.[key]?.clips ?? [];
	if (youtubeClips.length) return youtubeClips;

	const dailymotionClips = titleNode?.[key]?.dailymotionClips ?? [];
	return dailymotionClips.length ? [dailymotionClips[0]] : [];
}

function toggleTextRecommendationModal(value: boolean) {
	if (preventNonSignedInAction(AccountModes.REGISTRATION_OPTIONS)) return;

	displayTextRecommendationModal.value = value;
	displayTextRecommendationModal.value ? disableScroll() : enableScroll();
}

const textRecommendationArray = computed(() => props.titleDetailNode?.content?.textRecommendations ?? []);

// SINGLE_QTV_EXP
const { allRegularOffers } = useBuyBoxData({
	buyboxData: () => buyboxOffers.value as BuyBoxOffers,
	title: () => titleDetailNodeComputed.value as TitleDetail,
	buyboxFilter: () => BuyBoxFilterOptions.ALL,
	isAVODActive: () => props.isAVODActive,
});
const { inPromotionCatalogue, promotionOffer } = usePromotion({
	offers: allRegularOffers.value,
	title: () => titleDetailNodeComputed.value as TitleDetail,
});
const singleQtvActive = computed(() => {
	return (
		!inPromotionCatalogue.value &&
		promotionOffer.value?.package.packageId === 9 &&
		!isTitleUpcoming.value &&
		newBuyboxView.value === NewBuyBoxView.LIST
	);
});
const { trackSingleQtvExp, expActiveVariant } = useSingleQtvExp(singleQtvActive);
watch(expActiveVariant, () => {
	if (expActiveVariant.value) {
		trackSingleQtvExp({
			action: 'impression',
		});
	}
});
//SINGLE_QTV_EXP

return { __sfc: true,TitleDetailLazyWhyToWatchV1, TitleDetailStreamingChartV1, TitleDetailStreamingChartV2, TitleSynopsis, TitleDetailAccordion, TitleDetailArticleBlockV1, TitleDetailProductionNewsBlockV1, ArticleBlock, UpcomingContent, EpisodesList, TitleCredits, SpinningText, TitleDetailSimilarTitles, TitleDetailSimilarTitlesV2, BuyBox, TitleDetailQuickActionBarV1, activeClipId, streamingChart, container, displayTextRecommendationModal, titlePageActiveVariant, newBuyboxView, isAdmin, isEditor, profileIsComplete, profileInfo, loggedInExpBuyboxView, isSeoUser, country, t, isLoggedIn, emits, props, titleDetailNodeComputed, title, titleName, titleId, titleObjectId, seasonName, seasonNumber, seasonTotalEpisodeCount, isShow, isSeason, recentEpisodes, recentEpisodesAvailable, isTitleUpcoming, showDetails, originalTitle, isInWatchList, textRecommendationUserCountries, buyboxOffers, hasTrailers, isListSponsoredAd, isShadowCampaign, hasActiveSRCampaign, streamingCharts, displayStreamingChart, visibleSectionState, showSynopsis, showSimilarTitles, aboutHeadline, showNewBuyBox, episodesTitle, maxEpisodes, episodes, showTextRecommendationTools, displayThirdAd, titleClips, getTitleClips, toggleTextRecommendationModal, textRecommendationArray, allRegularOffers, inPromotionCatalogue, promotionOffer, singleQtvActive, trackSingleQtvExp, expActiveVariant, AdUnit, LazyClientShow, SimilarTitles, BuyBoxNew, GuideList, SectionContainer, TitleDetailJumpLinksV1, BuyBoxBanner, TitleDetailTitleClipsV1, isTitleSeason, isTitleShow, TitleDetailHeroV1, TitleDetailTitleSidebarV1, BuyBoxTypeSwitcher, PromotedBuyBox, TextRecommendationModal, TextRecommendations, SeasonListV1 }
}

})